import { Component, ErrorHandler, Input, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { ValidatorFn, Validators } from '@angular/forms'
import { FieldTree, form, metadata as kentanMetatieto, REQUIRED, required, validate } from '@angular/forms/signals'

import { Tilikausi } from '@lemon/core-firebase/model/kayttaja'
import {
  DownloadYhtiokokouksenPoytakirjaPdfRequest,
  DownloadYhtiokokouksenPoytakirjaPdfResponse,
  SaveYhtiokokouksenPoytakirjaDataRequest,
  SaveYhtiokokouksenPoytakirjaDataResponse,
  SignYhtiokokousRequest,
  SignYhtiokokousResponse,
  TilinpaatosMetadata,
  TilinpaatosOsakasUser,
  TilinpaatosSigningData,
  TilinpaatosUserData,
  YhtiokokouksenPoytakirjaData,
  YhtiokokousParticipant,
  YhtiokokousRole,
} from '@lemon/core-firebase/model/tilinpaatos'
import { LocalDateTime, Timestamp } from '@lemon/core/model/common'

import { TilinpaatosUriService } from '@lemon/core-firebase/service/tilinpaatos-uri.service'
import { DateService } from '@lemon/core/service/date.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from 'app/_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'

import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, of as observableOf, Subject, switchMap, takeUntil } from 'rxjs'
import { ImageCacheClass, ImageCacheClassPreRendered } from './tilinpaatokset.component'

import { MatDialog } from '@angular/material/dialog'
import { CurrencyService } from '@lemon/core/service/currency.service'
import { PdfService } from 'app/_angular/service/pdf.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { FormValidators } from 'app/_jaettu-angular/_validators/FormValidators'
import { DocumentSnapshot } from 'firebase/firestore'

import { DomSanitizer } from '@angular/platform-browser'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { PoytakirjaSignedDialog, PoytakirjaSignedDialogData } from './poytakirja-signed.dialog'

interface YhtiokokousUserWithFullName extends Pick<TilinpaatosOsakasUser, 'lemonaidUid' | 'osakkeita'> {
  name: string
}
interface YhtiokokousParticipantPlusSigningStatus {
  name: string
  rolesLocalized: string
  signedAt: Timestamp
  isCurrentUser: boolean
}

interface PoytakirjaLomake {
  meetingDate: Date | null
  meetingHours: number | null
  meetingMinutes: number | null
  kokouspaikka: string
  poytakirjanNumero: string
  lasnaolijat: LasnaolijaLomake[]
  kokouksenAvasi: string | null
  puheenjohtaja: YhtiokokousUserWithFullName | null
  poytakirjanTarkastaja: YhtiokokousUserWithFullName | null
  osinkojaJaetaan: boolean | null
}

interface LasnaolijaLomake {
  selected: boolean
  name: string
  osakkeita: number | null
  lemonaidUid: string | null
}

interface ViewData {
  tilintarkastusDone: boolean
  tilinpaatosSigned: boolean
  ptkirjaMissingSignatures: boolean
  mode: 'loading' | 'view' | 'edit'
  pdfPages: ImageCacheClass[]
  startPdfImageRendering: () => Promise<void>
}

@Component({
  selector: 'app-reports-yhtiokokouksen-poytakirja',
  templateUrl: './yhtiokokouksen-poytakirja.component.html',
  styleUrls: ['./tilinpaatokset.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class YhtiokokouksenPoytakirjaComponent implements OnInit, OnDestroy {
  @Input() selectedTilikausiObservable: Observable<Tilikausi>
  @Input() tilinpaatosUsersObservable: Observable<TilinpaatosUserData>
  @Input() tilinpaatosSnapshotObservable: Observable<{ tilikausi: Tilikausi; snap: DocumentSnapshot<TilinpaatosMetadata> }>

  readonly commonError = signal<string>(undefined)
  readonly selectedParticipants = signal<YhtiokokousUserWithFullName[]>([])

  viewDataObservable: Observable<ViewData>
  // tilintarkastusTehtyObservable: Observable<{ done: boolean }>
  // tilinpaatosSignedObservable: Observable<{ signed: boolean }>
  // poytakirjaMissingSignaturesObservable: Observable<boolean>
  osinkojaJaetaanYhteensaObservable: Observable<string>

  private _poytakirja: YhtiokokouksenPoytakirjaData
  private _meetingTimeAndDate: LocalDateTime
  readonly signers = signal<YhtiokokousParticipantPlusSigningStatus[]>([])

  private _editingPoytakirjaSubject: BehaviorSubject<'loading' | 'view' | 'edit'> = new BehaviorSubject('loading')
  private _poytakirjaPreviewPdfAsBase64Observable: Observable<{ base64: string; tilikausi: Tilikausi }>

  private _yhtiokokousUsersObservable: Observable<YhtiokokousUserWithFullName[]>
  yhtiokokousUsers: YhtiokokousUserWithFullName[]

  private _saveStarted: boolean = false

  readonly usersWithoutLemonaidAccountSelected = signal<boolean>(undefined)

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private _lasnaolijatLopetus = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _kokouksenPaivamaaranValidaattori = signal<ValidatorFn>(() => null)

  lomake = form(
    signal<PoytakirjaLomake>({
      meetingDate: null,
      meetingHours: null,
      meetingMinutes: null,
      kokouspaikka: '',
      poytakirjanNumero: '',
      lasnaolijat: [],
      kokouksenAvasi: null,
      puheenjohtaja: null,
      poytakirjanTarkastaja: null,
      osinkojaJaetaan: null,
    }),
    polku => {
      validoiReaktiivisesti(polku.meetingDate, kontrolli => this._kokouksenPaivamaaranValidaattori()(kontrolli))
      required(polku.meetingDate)
      required(polku.meetingHours)
      required(polku.meetingMinutes)
      required(polku.kokouspaikka)
      required(polku.poytakirjanNumero)
      validate(polku.lasnaolijat, ({ value }) => (value().length < 1 ? { kind: 'min' } : null))
      required(polku.kokouksenAvasi)
      required(polku.puheenjohtaja)
      required(polku.poytakirjanTarkastaja)
      // Signal Formsin required() hylkää arvon false (osinkoa ei jaeta), reaktiivinen Validators.required hyväksyi sen
      kentanMetatieto(polku.osinkojaJaetaan, REQUIRED, () => true)
      validoiReaktiivisesti(polku.osinkojaJaetaan, Validators.required)
    },
  )

  constructor(
    private _tilinpaatosUriService: TilinpaatosUriService,
    private _validationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _dateService: DateService,
    private _errorHandler: ErrorHandler,
    private _translationService: LemonTranslationService,
    private _kayttajaService: KayttajaService,
    private _pdfService: PdfService,
    private _currencyService: CurrencyService,
    private _dialog: MatDialog,
    private _domSanitizer: DomSanitizer,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _fileSaverService: FileSaverService,
  ) {}

  private _isTilinpaatosSigned(signaturesData: TilinpaatosSigningData): boolean {
    if (!signaturesData?.signs?.length) {
      return false
    }
    return signaturesData.signs.filter(signer => !signer.signedAt).length === 0
  }

  private _isPoytakirjaMissingSignatures(poytakirja: YhtiokokouksenPoytakirjaData): boolean {
    if (!poytakirja?.signs?.length) {
      return true
    }
    return poytakirja.signs.filter(sign => !sign.signedAt).length > 0
  }

  private _isTilintarkastusDone(tilinpaatos: TilinpaatosMetadata): boolean {
    if (!tilinpaatos) {
      return true
    }
    if (tilinpaatos.tilintarkastetaan && !tilinpaatos.tilintarkastusTehty) {
      return false
    }
    return true
  }

  ngOnInit() {
    // this.tilintarkastusTehtyObservable = this.tilinpaatosSnapshotObservable.pipe(
    //   map(snap => {
    //     const data = snap?.data()
    //     if (!data) {
    //       return null
    //     }
    //     if (data.tilintarkastetaan && !data.tilintarkastusTehty) {
    //       return { done: false }
    //     }
    //     return { done: true }
    //   })
    // )

    const tilinpaatosSignaturesObservable: Observable<{ snap: DocumentSnapshot<TilinpaatosSigningData> }> = combineLatest([
      this.tilinpaatosSnapshotObservable,
      this._kayttajaService.kayttajanTiedotObservable,
    ]).pipe(
      switchMap(([snap, kayttaja]) => {
        // If we don't have the snap (data was not loaded from database), return null
        if (!snap?.snap || !snap?.tilikausi || !kayttaja) {
          return observableOf<{ snap: DocumentSnapshot<TilinpaatosSigningData> }>(null)
        }

        // If we have the snap, but no data, send and object containing null as snap.
        // This is needed to distinguish the situation where we are still loading vs not
        // signed down the line to not confuse our loading indicators, so do NOT "optimize" this away...
        const metadata = snap.snap.data()
        if (!metadata) {
          return observableOf<{ snap: DocumentSnapshot<TilinpaatosSigningData> }>({ snap: null })
        }

        const uri = this._tilinpaatosUriService.getTilinpaatosSignaturesDocUri(kayttaja.asiakasAvain, metadata.viimeisinTilinpaatosVersioAvain)
        return this._firebaseLemonaid
          .firestoreDoc<TilinpaatosSigningData>(uri)
          .listenSnap()
          .pipe(
            map(signingSnap => {
              return { snap: signingSnap }
            }),
          )
      }),
    )

    // this.tilinpaatosSignedObservable = tilinpaatosSignaturesObservable.pipe(
    //   map(signingDataDoc => {
    //     if (!signingDataDoc) {
    //       return null
    //     }
    //     const signingData = signingDataDoc.data()
    //     if (!signingData?.signs?.length) {
    //       return { signed: false }
    //     }
    //     return { signed: signingData.signs.filter(signer => !signer.signedAt).length === 0 }
    //   })
    // )

    const poytakirjaObservable: Observable<{ tilikausi: Tilikausi; snap: DocumentSnapshot<YhtiokokouksenPoytakirjaData> }> = combineLatest([
      this._kayttajaService.kayttajanTiedotObservable,
      this.selectedTilikausiObservable,
    ]).pipe(
      switchMap(([kayttaja, tilikausi]) => {
        if (!kayttaja || !tilikausi) {
          return observableOf(null)
        }
        const uri = this._tilinpaatosUriService.getYhtiokokouksenPoytakirjaDocUri(kayttaja.asiakasAvain, tilikausi.avain)
        return this._firebaseLemonaid
          .firestoreDoc<YhtiokokouksenPoytakirjaData>(uri)
          .listenSnap()
          .pipe(
            map(snap => {
              return {
                snap: snap,
                tilikausi: tilikausi,
              }
            }),
          )
      }),
    )

    this._poytakirjaPreviewPdfAsBase64Observable = combineLatest([poytakirjaObservable, this._translationService.currentLanguageObservable]).pipe(
      switchMap(([poytakirjaData, lang]) => {
        if (!poytakirjaData || !poytakirjaData.tilikausi) {
          return observableOf<{ tilikausi: Tilikausi; base64: string }>(null)
        }
        const poytakirja = poytakirjaData.snap?.data()
        if (!poytakirja || !poytakirja.avain || !lang) {
          return observableOf<{ tilikausi: Tilikausi; base64: string }>({ tilikausi: poytakirjaData.tilikausi, base64: null })
        }
        const req: DownloadYhtiokokouksenPoytakirjaPdfRequest = {
          pdfAvain: poytakirja.finalPdfAvain ? poytakirja.finalPdfAvain : poytakirja.avain,
          tilikausiAvain: poytakirja.tilikausi.avain,
          kieli: lang,
        }
        return this._firebaseLemonaid.functionsCall<DownloadYhtiokokouksenPoytakirjaPdfRequest, DownloadYhtiokokouksenPoytakirjaPdfResponse>('yhtiokokousPdfDownload', req).then(resp => {
          if (!resp) {
            return { tilikausi: poytakirjaData.tilikausi, base64: null }
          }
          if (resp.e) {
            this._errorHandler.handleError(new Error('Yhtiökokous pöytäkirja PDF preview download failed! ' + resp.e))
            return { tilikausi: poytakirjaData.tilikausi, base64: null }
          }
          return { tilikausi: poytakirjaData.tilikausi, base64: resp.base64Pdf }
        })
      }),
    )

    const pdfPagesObservable: Observable<{ pages: ImageCacheClass[]; tilikausi: Tilikausi }> = this._poytakirjaPreviewPdfAsBase64Observable.pipe(
      switchMap(async base64Data => {
        if (!base64Data || !base64Data.tilikausi) {
          return null
        }
        if (!base64Data.base64) {
          return { pages: [], tilikausi: base64Data.tilikausi }
        }
        const arrayBuffer = this._fileSaverService.base64StringToUint8Array(base64Data.base64)
        const openData = await this._pdfService.isPdfOpenableRenderAllToObjectUrl(arrayBuffer, 720)
        if (openData?.success) {
          const sivut: ImageCacheClass[] = []
          for (const renderedPage of openData.renderedPages) {
            // Create firestore tosite image metadata
            sivut.push(new ImageCacheClassPreRendered(this._domSanitizer.bypassSecurityTrustUrl(renderedPage) as string))
          }
          return { pages: sivut, tilikausi: base64Data.tilikausi }
        }
        return { pages: [], tilikausi: base64Data.tilikausi }
      }),
    )

    this.viewDataObservable = combineLatest([this.tilinpaatosSnapshotObservable, tilinpaatosSignaturesObservable, this._editingPoytakirjaSubject, pdfPagesObservable, poytakirjaObservable]).pipe(
      switchMap(async ([tilinpaatosMetadataSnapshot, tilinpaatosSignaturesSnapshot, editMode, pdfData, poytakirjaData]) => {
        if (
          !tilinpaatosMetadataSnapshot ||
          !tilinpaatosMetadataSnapshot.tilikausi ||
          !tilinpaatosMetadataSnapshot.snap ||
          !pdfData ||
          !tilinpaatosSignaturesSnapshot ||
          !poytakirjaData ||
          pdfData.tilikausi.avain !== tilinpaatosMetadataSnapshot.tilikausi.avain
        ) {
          return null
        }

        const ptkirjaData = poytakirjaData.snap?.data()
        const hasForceEditModeOn = !!ptkirjaData?.forceEditMode

        const tilinpaatosMetadata = tilinpaatosMetadataSnapshot.snap.data()
        const signaturesData = tilinpaatosSignaturesSnapshot?.snap?.data()

        const viewData: ViewData = {
          mode: hasForceEditModeOn ? 'edit' : editMode,
          tilinpaatosSigned: this._isTilinpaatosSigned(signaturesData),
          tilintarkastusDone: this._isTilintarkastusDone(tilinpaatosMetadata),
          ptkirjaMissingSignatures: this._isPoytakirjaMissingSignatures(ptkirjaData),
          pdfPages: pdfData.pages,
          startPdfImageRendering: null,
          // startPdfImageRendering: async () => {
          //   await this._sleep(50)
          //   for (const page of pdfPages) {
          //     await page.startImageRendering()
          //     await this._sleep(50)
          //   }
          // }
        }
        // await viewData.startPdfImageRendering()
        let renderHappened = false
        // await this._sleep(50)
        for (const page of pdfData.pages) {
          const renderedSomething = await page.startImageRendering()
          if (renderedSomething) {
            renderHappened = true
            await this._sleep(50)
          }
        }
        if (renderHappened) {
          await this._sleep(750)
        }
        return viewData
      }),
    )

    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this._ngUnsubscribe))

    muutokset(this.lomake.meetingDate).subscribe(val => {
      if (val) {
        this._checkMeetingTimeExists()
        const asLocalDate = this._dateService.dateToLocalDate(val)
        this._meetingTimeAndDate.year = asLocalDate.year
        this._meetingTimeAndDate.month = asLocalDate.month
        this._meetingTimeAndDate.day = asLocalDate.day
      }
    })

    muutokset(this.lomake.meetingHours).subscribe(val => {
      this._checkMeetingTimeExists()
      this._meetingTimeAndDate.hour = val
    })

    muutokset(this.lomake.meetingMinutes).subscribe(val => {
      this._checkMeetingTimeExists()
      this._meetingTimeAndDate.minutes = val
    })

    muutokset(this.lomake.kokouspaikka).subscribe(val => (this._poytakirja ? (this._poytakirja.kokouspaikka = val) : null))
    muutokset(this.lomake.poytakirjanNumero).subscribe(val => (this._poytakirja ? (this._poytakirja.poytakirjanNumero = val) : null))
    muutokset(this.lomake.kokouksenAvasi).subscribe(val => (this._poytakirja ? (this._poytakirja.kokouksenAvasi = val) : null))
    muutokset(this.lomake.puheenjohtaja).subscribe(val => (this._poytakirja ? (this._poytakirja.puheenjohtaja = val?.name) : null))
    // this.sihteeri.valueChanges.pipe(takeUntil(this._ngUnsubscribe)).subscribe(val => this._poytakirja ? this._poytakirja.sihteeri = val : null)
    muutokset(this.lomake.poytakirjanTarkastaja).subscribe(val => (this._poytakirja ? (this._poytakirja.poytakirjanTarkastaja = val?.name) : null))
    muutokset(this.lomake.osinkojaJaetaan).subscribe(val => (this._poytakirja ? (this._poytakirja.osinkojaJaetaan = val) : null))

    this._yhtiokokousUsersObservable = this.tilinpaatosUsersObservable.pipe(
      map(userData => {
        const output: YhtiokokousUserWithFullName[] = []
        for (const user of userData?.osakkaatUsers || []) {
          const userWithFullName: YhtiokokousUserWithFullName = {
            name: user.etunimi + ' ' + user.sukunimi,
            osakkeita: user.osakkeita,
            lemonaidUid: user.lemonaidUid,
          }
          output.push(userWithFullName)
        }
        return output
      }),
    )

    // Set default values during first load
    this._yhtiokokousUsersObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(users => {
      this._lasnaolijatLopetus.next()
      asetaArvo(this.lomake.lasnaolijat, [], { ilmoita: false })
      if (!users?.length) {
        return
      }
      this.yhtiokokousUsers = users
      this.selectedParticipants.set([])
      for (const user of users) {
        if (user.lemonaidUid) {
          this.selectedParticipants.update(valitut => [...valitut, user])
        }
        this._lisaaLasnaolija(user)
      }
    })

    tilinpaatosSignaturesObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(snap => {
      const tilinpaatosSignatures = snap?.snap?.data()

      if (tilinpaatosSignatures?.signs?.length) {
        const latestSign = Math.max(...tilinpaatosSignatures.signs.map(s => s.signedAt?.toMillis() ?? 4102437599000)) // Fallback is 31.12.2099
        const latestSignMinusOneDay = this._dateService.lisaaPaivia(new Date(latestSign), -1)
        this._kokouksenPaivamaaranValidaattori.set(FormValidators.minDateValidator(latestSignMinusOneDay))
      } else {
        this._kokouksenPaivamaaranValidaattori.set(() => null)
      }
    })

    combineLatest([poytakirjaObservable, this._kayttajaService.kayttajanTiedotObservable, this.selectedTilikausiObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([poytakirjaData, currentUser, selectedTilikausi]) => {
        if (!currentUser || !selectedTilikausi || !poytakirjaData?.snap) {
          this.signers.set([])
          this._poytakirja = null
          this._editingPoytakirjaSubject.next('loading')
          return
        }

        const poytakirja = poytakirjaData.snap.data()
        if (poytakirja) {
          const signs = poytakirja.signs ?? []
          this._editingPoytakirjaSubject.next(signs.length ? 'view' : 'edit')
          this._poytakirja = poytakirja
          this.signers.set(
            signs.map(signer => {
              const withExtraData: YhtiokokousParticipantPlusSigningStatus = {
                isCurrentUser: signer.user.lemonaidUid === currentUser.uid,
                rolesLocalized: signer.user.roles?.map(role => this._translationService.lokalisoi('reports-lemonaid.' + role)).join('\n') ?? '',
                signedAt: signer.signedAt,
                name: signer.user.name,
              }
              return withExtraData
            }),
          )
        } else {
          this._poytakirja = this._createEmptyPoytakirja(selectedTilikausi)
          this._editingPoytakirjaSubject.next('edit')
        }

        if (this._poytakirja.dateAndTime) {
          this._meetingTimeAndDate = this._dateService.numberTimestampToLocalDateTime(this._poytakirja.dateAndTime)
          asetaArvo(this.lomake.meetingDate, this._dateService.numberTimestampToDate(this._poytakirja.dateAndTime))
          asetaArvo(this.lomake.meetingHours, this._meetingTimeAndDate.hour)
          asetaArvo(this.lomake.meetingMinutes, this._meetingTimeAndDate.minutes)
        } else {
          asetaArvo(this.lomake.meetingDate, null)
          asetaArvo(this.lomake.meetingHours, null)
          asetaArvo(this.lomake.meetingMinutes, null)
        }

        asetaArvo(this.lomake.kokouspaikka, this._poytakirja.kokouspaikka ?? '')
        asetaArvo(this.lomake.poytakirjanNumero, this._poytakirja.poytakirjanNumero ?? '')
        asetaArvo(this.lomake.kokouksenAvasi, this._poytakirja.kokouksenAvasi ?? null)
        asetaArvo(this.lomake.puheenjohtaja, this.selectedParticipants()?.find(participants => participants.name === this._poytakirja.puheenjohtaja) || null)
        // this.sihteeri.setValue(poytakirja.sihteeri)
        asetaArvo(this.lomake.poytakirjanTarkastaja, this.selectedParticipants()?.find(participants => participants.name === this._poytakirja.poytakirjanTarkastaja) || null)
        asetaArvo(this.lomake.osinkojaJaetaan, this._poytakirja.osinkojaJaetaan ?? null)

        this._tryRadioButtonAutoselect(this.selectedParticipants())
      })

    this.osinkojaJaetaanYhteensaObservable = this.tilinpaatosSnapshotObservable.pipe(
      map(snap => {
        const data = snap?.snap?.data()
        // Ignore if dividend sum is 0
        if (data?.osinkojaYhteensa) {
          return this._currencyService.formatoiRahaIlmanValuuttaSymbolia(data.osinkojaYhteensa, 'fi')
        }
        return null
      }),
    )
  }

  protected _sleep(millis: number): Promise<any> {
    return new Promise(resolve => {
      setTimeout(resolve, millis)
    })
  }

  private _checkMeetingTimeExists() {
    if (!this._meetingTimeAndDate) {
      const asLocalDate = this._dateService.dateToLocalDate(this.lomake.meetingDate().value() ?? new Date())
      this._meetingTimeAndDate = {
        year: asLocalDate.year,
        month: asLocalDate.month,
        day: asLocalDate.day,
        hour: this.lomake.meetingHours().value() ?? null,
        minutes: this.lomake.meetingMinutes().value() ?? null,
        seconds: 1,
      }
    }
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  async createPreviewMoveToSigning() {
    if (!this.selectedParticipants()?.length) {
      this.commonError.set(this._translationService.lokalisoi('reports-lemonaid.yhtiokokous-no-signers-error'))
      return
    }

    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      this._validationService.naytaEnsimmainenVirhe()
      return
    }

    if (this._saveStarted) {
      return
    }

    this._saveStarted = true

    try {
      const allekirjoituksiaAnnettu = this._poytakirja.signs?.some(signer => !!signer.signedAt)
      const varoitaAllekirjoitustenPoistumisesta = allekirjoituksiaAnnettu && !this._poytakirja.forceEditMode
      if (varoitaAllekirjoitustenPoistumisesta) {
        const areYouSureData: AreYouSureDialogData = {
          rightAction: this._translationService.lokalisoi('yleiset.jatka'),
          text: this._translationService.lokalisoi('reports-lemonaid.muokkaus-poistaa-allekirjoitukset'),
        }
        const isSure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: areYouSureData }).afterClosed())
        if (!isSure) {
          return
        }
      }

      this._ladataanService.aloitaLataaminen()

      const puheenjohtaja: YhtiokokousUserWithFullName = this.lomake.puheenjohtaja().value()
      const tarkastaja: YhtiokokousUserWithFullName = this.lomake.poytakirjanTarkastaja().value()

      if (this.selectedParticipants().length > 1 && puheenjohtaja.lemonaidUid === tarkastaja.lemonaidUid) {
        this.commonError.set(this._translationService.lokalisoi('reports-lemonaid.valitse-eri-henkilot'))
        return
      }

      const tilinpaatos = await firstValueFrom(this.tilinpaatosSnapshotObservable.pipe(map(snap => snap?.snap?.data() || null)))
      this._poytakirja.osinkojaJaetaanYhteensa = tilinpaatos.osinkojaYhteensa
      this._poytakirja.dateAndTime = this._dateService.localDateTimeToNumberTimestamp(this._meetingTimeAndDate)
      this._poytakirja.lasnaolijat = this.selectedParticipants().map(selected => {
        const lasnaolija: YhtiokokousParticipant = {
          lemonaidUid: selected.lemonaidUid,
          osakkeita: selected.osakkeita,
          name: selected.name,
          roles: null,
        }
        return lasnaolija
      })
      // this._poytakirja.signs = this.selectedParticipants?.map(selected => {
      //   const userData: YhtiokokousParticipant = {
      //     name: selected.name,
      //     lemonaidUid: selected.lemonaidUid,
      //     osakkeita: selected.osakkeita,
      //     role: this._getRoleFromPoytakirja(selected, this._poytakirja)
      //   }
      //   return { user: userData, signedAt: null }
      // }) ?? []

      const signersWithRoles: { user: YhtiokokousUserWithFullName; roles: YhtiokokousRole[] }[] =
        puheenjohtaja.lemonaidUid === tarkastaja.lemonaidUid
          ? [{ user: puheenjohtaja, roles: ['puheenjohtaja', 'poytakirjan-tarkastaja'] }]
          : [
              { user: puheenjohtaja, roles: ['puheenjohtaja'] },
              { user: tarkastaja, roles: ['poytakirjan-tarkastaja'] },
            ]
      // Note! Make sure the order stays the same so that the index-based role logic works
      this._poytakirja.signs = signersWithRoles.map(signer => {
        const userData: YhtiokokousParticipant = {
          name: signer.user.name,
          lemonaidUid: signer.user.lemonaidUid,
          osakkeita: signer.user.osakkeita,
          roles: signer.roles,
        }
        return { user: userData, signedAt: null }
      })
      this._poytakirja.forceEditMode = false

      const request: SaveYhtiokokouksenPoytakirjaDataRequest = {
        poytakirja: this._poytakirja,
      }

      const resp = await this._firebaseLemonaid.functionsCall<SaveYhtiokokouksenPoytakirjaDataRequest, SaveYhtiokokouksenPoytakirjaDataResponse>('yhtiokokousSaveAndConvertPoytakirja', request)
      if (!resp || resp.e) {
        throw new Error('Yhtiökokouksen pöytäkirja save failed! ' + (resp?.e || 'No response'))
      } else {
        this._editingPoytakirjaSubject.next('view')
      }
    } catch (err: any) {
      this._errorHandler.handleError(err)
      this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._saveStarted = false
    }
  }

  private _lisaaLasnaolija(user: YhtiokokousUserWithFullName) {
    const index = this.lomake.lasnaolijat().value().length
    // FormArray.push ei lähettänyt aiempien rivien valueChanges-tapahtumia
    asetaArvo(
      this.lomake.lasnaolijat,
      [...this.lomake.lasnaolijat().value(), { selected: !!user.lemonaidUid, name: user.name, osakkeita: user.osakkeita ?? null, lemonaidUid: user.lemonaidUid ?? null }],
      { ilmoita: false },
    )

    arvonMuutokset(this.lomake.lasnaolijat[index].selected, { injector: this._injector })
      .pipe(takeUntil(this._lasnaolijatLopetus), takeUntil(this._ngUnsubscribe))
      .subscribe(isSelected => {
        if (isSelected) {
          this.commonError.set(null)
          this.selectedParticipants.update(valitut => [...valitut, user])
        } else {
          this.selectedParticipants.update(valitut => {
            const idx = valitut.findIndex(sp => sp.lemonaidUid === user.lemonaidUid)
            const jaljelle = [...valitut]
            jaljelle.splice(idx, 1)
            return jaljelle
          })

          if (this.lomake.kokouksenAvasi().value() === user.name) {
            asetaArvo(this.lomake.kokouksenAvasi, null)
          }
          if (this.lomake.puheenjohtaja().value()?.lemonaidUid === user.lemonaidUid) {
            asetaArvo(this.lomake.puheenjohtaja, null)
          }
          if (this.lomake.poytakirjanTarkastaja().value()?.lemonaidUid === user.lemonaidUid) {
            asetaArvo(this.lomake.poytakirjanTarkastaja, null)
          }
        }
        this.usersWithoutLemonaidAccountSelected.set(this.selectedParticipants().findIndex(sp => !sp.lemonaidUid) > -1)

        this._tryRadioButtonAutoselect(this.selectedParticipants())
      })
  }

  /** Attempts to autoselect Kokouksen avasi, Puheenjohtaja & Pöytäkirjan tarkastaja radio fields if there's only one participant and it has a connected Lemonaid account */
  private _tryRadioButtonAutoselect(selectedParticipants: YhtiokokousUserWithFullName[]) {
    const onlyParticipant = selectedParticipants?.length === 1 ? selectedParticipants[0] : null
    if (!onlyParticipant) {
      return
    }
    if (!onlyParticipant.lemonaidUid) {
      // No corresponding Lemonaid user, can't be autoselected
      return
    }
    // If only one participant, autoselected that in the radios below
    if (!this.lomake.kokouksenAvasi().value()) {
      asetaArvo(this.lomake.kokouksenAvasi, onlyParticipant.name)
    }
    if (!this.lomake.poytakirjanTarkastaja().value()) {
      asetaArvo(this.lomake.poytakirjanTarkastaja, onlyParticipant)
    }
    if (!this.lomake.puheenjohtaja().value()) {
      asetaArvo(this.lomake.puheenjohtaja, onlyParticipant)
    }
  }

  async saveSignature() {
    if (await this._kayttajaService.kayttajaaImpersonoidaan()) {
      return
    }

    if (!!this._saveStarted) {
      return
    }

    this._saveStarted = true

    try {
      const areYouSureData: AreYouSureDialogData = {
        rightAction: this._translationService.lokalisoi('reports-lemonaid.allekirjoita'),
        header: this._translationService.lokalisoi('reports-lemonaid.allekirjoita-poytakirja'),
        text: this._translationService.lokalisoi('reports-lemonaid.ylla-oleva-poytakirja'),
      }
      const isSure = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data: areYouSureData }).afterClosed())
      if (!isSure) {
        return
      }

      this._ladataanService.aloitaLataaminen()

      const selectedTilikausi = await firstValueFrom(this.selectedTilikausiObservable)

      const request: SignYhtiokokousRequest = {
        tilikausiAvain: selectedTilikausi.avain,
      }

      const resp = await this._firebaseLemonaid.functionsCall<SignYhtiokokousRequest, SignYhtiokokousResponse>('yhtiokokousSigning', request)
      if (!resp || resp.e) {
        throw new Error('Yhtiökokous signing failed! ' + (resp?.e || 'No response'))
      } else {
        // All correct
        const isLastSigner = this._poytakirja.signs.filter(signer => !signer.signedAt).length <= 1
        if (isLastSigner) {
          const dialogData: PoytakirjaSignedDialogData = {
            year: selectedTilikausi.loppuu.year,
          }
          this._dialog.open(PoytakirjaSignedDialog, { data: dialogData })
        }
      }
    } catch (err: any) {
      this._errorHandler.handleError(err)
      this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
    } finally {
      this._ladataanService.lopetaLataaminen()
      this._saveStarted = false
    }
  }

  async downloadPdf() {
    this._ladataanService.aloitaLataaminen()

    try {
      const base64FilePromise = firstValueFrom(this._poytakirjaPreviewPdfAsBase64Observable)
      const asiakasPromise = firstValueFrom(this._kayttajaService.nykyinenAsiakasObservable)
      const tilikausiPromise = firstValueFrom(this.selectedTilikausiObservable)

      const [base64File, asiakas, tilikausi] = await Promise.all([base64FilePromise, asiakasPromise, tilikausiPromise])

      if (!base64File?.base64?.length) {
        throw new Error('Failed to get base64 file of pöytäkirja PDF')
      }

      const fileName = asiakas.nimi + ' - yhtiökokouksen pöytäkirja ' + tilikausi.loppuu.year + '.pdf'
      this._fileSaverService.saveBase64As(base64File.base64, fileName, 'pdf')
    } catch (err: any) {
      this._errorHandler.handleError(err)
      this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  editPoytakirja() {
    this._editingPoytakirjaSubject.next('edit')
  }

  // private _getRoleFromPoytakirja(user: YhtiokokousUserWithFullName, poytakirja: YhtiokokouksenPoytakirjaData): YhtiokokousRole {
  //   if (poytakirja.puheenjohtaja === user.name) { return 'puheenjohtaja' }
  //   // if (poytakirja.sihteeri) { return 'sihteeri' }
  //   if (poytakirja.poytakirjanTarkastaja === user.name) { return 'poytakirjan-tarkastaja' }
  //   return null
  // }

  private _createEmptyPoytakirja(tilikausi: Tilikausi): YhtiokokouksenPoytakirjaData {
    return {
      avain: null, // Leave empty, will be filled in backend
      created: null,
      year: tilikausi.loppuu.year,
      tilikausi: tilikausi,
      lasnaolijat: [],
      dateAndTime: null,
      kokouspaikka: null,
      poytakirjanNumero: '1/' + new Date().getFullYear(),
      kokouksenAvasi: null,
      puheenjohtaja: null,
      // sihteeri: null,
      poytakirjanTarkastaja: null,
      osinkojaJaetaan: null,
      osingotNostettavissa: null,
      signs: [],
      osinkojaJaetaanYhteensa: 0,
      voittoPaatosTextForPrh: null,
      forceEditMode: true,
    }
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }
}
